import {
  ReactNode,
  forwardRef,
  lazy,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';

import { QuestionMarkCircleIcon } from '@heroicons/react/24/solid';
import { CheckCircledIcon } from '@radix-ui/react-icons';
import { Avatar, DropdownMenu, Flex, Sidebar, Text } from '@raystack/apsara';
import { Organization, User } from '@raystack/frontier';
import { useFrontier } from '@raystack/frontier/react';
import { For } from '@studio/hoc';
import kebabCase from 'lodash/kebabCase';
import { useAnalytics } from 'use-analytics';

import { CartSidebarIcon } from '@src/components/CartSidebarIcon';
import { OrgAvatar, UserAvatar } from '@src/components/Custom';
import { Notifications } from '@src/containers/Notifications';
import NotificationIcon from '@src/containers/Notifications/sidebar-icon';
import { OrderDeskPromotionPopup } from '@src/containers/OrderDesk/OrderDeskPromotionPopup';
import { IdentityContext } from '@src/contexts/identity/context';
import { Show } from '@src/hoc';
import { useProjects } from '@src/hooks';
import {
  setCopilotChatId,
  toggleCoPilot,
  toggleNotifications
} from '@src/redux/_reducers/appReducer';
import { setIAMDialogState } from '@src/redux/_reducers/authReducer';
import { setActiveAoi } from '@src/redux/_reducers/projectReducer';
import { setIsSidebarOpen } from '@src/redux/_reducers/sourceActionReducer';
import { useKeyPressBase } from '@src/useKeypress';
import { SubscriptionStateMap } from '@src/utils/constants/APP';
import { AURORA_INTELLIGENCE_THUMBNAIL_16 } from '@src/utils/constants/COPILOT_CONSTANTS';
import { ROUTES } from '@src/utils/constants/Routes';
import { LogoutUser } from '@src/utils/userHandler';

import { ReactComponent as VisualizeIcon } from './new_icons/aoi.svg';
import { ReactComponent as CatalogIcon } from './new_icons/catalog.svg';
import { ReactComponent as ExploreIcon } from './new_icons/explore.svg';
import { ReactComponent as MarketplaceIcon } from './new_icons/marketplace.svg';
import { ReactComponent as OrderDeskIcon } from './new_icons/order-desk.svg';
import { ReactComponent as RocketIcon } from './new_icons/rocket-filled.svg';
import { ReactComponent as WorkflowIcon } from './new_icons/workflows.svg';

import styles from './sidebar.module.css';

const IAMDialog = lazy(() => import('../Auth/IAMDialog'));

const CustomDropdownContent = ({
  dropdownItems,
  dataTestPrefix
}: {
  dropdownItems: {
    label?: string | ReactNode;
    menus?: Record<string, unknown>[];
  }[];
  dataTestPrefix: string;
}) => {
  return (
    <DropdownMenu.Content className={styles.dropdownContent}>
      <For each={dropdownItems}>
        {({ label, menus = [] }, i) => (
          <>
            <DropdownMenu.Group className={styles.dropdownGroup}>
              <Show when={!!label}>
                <DropdownMenu.Label>{label}</DropdownMenu.Label>
              </Show>
              <For each={menus}>
                {menu => (
                  <DropdownMenu.Item
                    key={menu.label as string}
                    leadingIcon={menu.leadingIcon as ReactNode}
                    trailingIcon={menu.trailingIcon as ReactNode}
                    onClick={menu.action as VoidFunction}
                    data-test-id={
                      menu.key
                        ? `${dataTestPrefix}${kebabCase(menu.key as string)}`
                        : undefined
                    }
                  >
                    {menu.label as string | ReactNode}
                  </DropdownMenu.Item>
                )}
              </For>
            </DropdownMenu.Group>
            <Show when={i + 1 < dropdownItems?.length}>
              <DropdownMenu.Separator />
            </Show>
          </>
        )}
      </For>
    </DropdownMenu.Content>
  );
};

const OrgDropDown = ({
  organizations,
  activeOrg,
  user
}: {
  organizations?: Organization[];
  activeOrg?: Organization;
  user?: User;
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const analytics = useAnalytics();

  function onOrgClick(org: Organization) {
    dispatch(setActiveAoi({}));
    dispatch(setCopilotChatId(null));
    setShowDropdown(false);
    navigate(`/${org.name}`);
  }

  function onOrgSettingClick() {
    dispatch(setIAMDialogState({ route: '/', isOpen: true }));
    setShowDropdown(false);
  }

  function logout() {
    analytics.reset();
    LogoutUser(navigate, true);
  }

  const dropdownItems = useMemo(
    () => [
      {
        label: user?.email,
        menus: organizations?.map(org => ({
          key: `workspace-${org.name}`,
          leadingIcon: <OrgAvatar org={org} size={1} />,
          label: org.title,
          action: () => onOrgClick(org),
          trailingIcon: org.id === activeOrg?.id && (
            <CheckCircledIcon color='var(--rs-color-foreground-accent-primary)' />
          )
        }))
      },
      {
        menus: [
          {
            key: 'settings',
            label: 'Workspace settings',
            action: onOrgSettingClick
          },
          {
            key: 'create-join-workspace',
            label: 'Create or join a Workspace',
            action: () =>
              navigate(`/${ROUTES.WORKSPACES}`, {
                state: { prevOrg: activeOrg?.name }
              })
          },
          {
            key: 'add-account',
            label: (
              <>
                Add an account <Text variant='secondary'>(coming soon)</Text>
              </>
            ),
            action: () => {}
          }
        ]
      },
      {
        menus: [
          {
            key: 'logout',
            label: `Logout`,
            action: logout
          }
        ]
      }
    ],
    [activeOrg?.id, navigate, organizations, user?.email, activeOrg?.name]
  );

  return (
    <DropdownMenu
      open={showDropdown}
      onOpenChange={setShowDropdown}
      placement='bottom-end'
    >
      <DropdownMenu.Trigger
        asChild
        data-test-id='sidebar-workspace-dropdown-trigger'
      >
        <Sidebar.Item
          classNames={{ root: styles.avatarWrapper }}
          leadingIcon={<OrgAvatar org={activeOrg!} size={3} />}
        >
          {activeOrg?.title}
        </Sidebar.Item>
      </DropdownMenu.Trigger>

      <CustomDropdownContent
        dropdownItems={dropdownItems}
        dataTestPrefix='sidebar-workspace-dropdown-'
      />
    </DropdownMenu>
  );
};

const ProfileDropDown = ({ user }: { user?: User }) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const analytics = useAnalytics();

  function onProfileSettingClick(e: React.MouseEvent) {
    e.preventDefault();
    dispatch(setIAMDialogState({ route: '/profile', isOpen: true }));
    setShowDropdown(false);
  }

  function logout() {
    analytics.reset();
    LogoutUser(navigate, true);
  }

  const dropdownItems = useMemo(
    () => [
      {
        menus: [
          {
            key: 'user-profile',
            leadingIcon: <UserAvatar size={5} radius='full' />,
            label: (
              <Flex direction='column'>
                <Text size='small' weight='medium'>
                  {user?.title}
                </Text>
                <Text size='mini' variant='tertiary' weight='medium'>
                  {user?.email}
                </Text>
              </Flex>
            ),
            action: () => {}
          }
        ]
      },
      {
        menus: [
          {
            key: 'settings',
            label: 'Profile Settings',
            action: onProfileSettingClick
          },
          {
            key: 'logout',
            label: `Logout`,
            action: logout
          }
        ]
      }
    ],
    [navigate, user?.email, user?.title]
  );

  return (
    <DropdownMenu
      open={showDropdown}
      onOpenChange={setShowDropdown}
      placement='right-start'
    >
      <DropdownMenu.Trigger
        asChild
        data-test-id='sidebar-profile-dropdown-trigger'
      >
        <Sidebar.Item
          classNames={{ root: styles.avatarWrapper }}
          leadingIcon={<UserAvatar size={3} className={styles.avatar} />}
        >
          {user?.email}
        </Sidebar.Item>
      </DropdownMenu.Trigger>
      <CustomDropdownContent
        dropdownItems={dropdownItems}
        dataTestPrefix='sidebar-profile-dropdown-'
      />
    </DropdownMenu>
  );
};

const SupportDropdown = () => {
  const dropdownItems = useMemo(
    () => [
      {
        menus: [
          {
            key: 'help',
            label: 'Help & Support',
            action: (e: React.MouseEvent) => {
              e.preventDefault();
              window.open(import.meta.env.VITE_CUSTMERSUPPORT_URL, '_blank');
            }
          },
          {
            key: 'documents',
            label: 'Documents',
            action: (e: React.MouseEvent) => {
              e.preventDefault();
              window.open(import.meta.env.VITE_USERGUIDE_URL, '_blank');
            }
          },
          {
            key: 'feedback',
            label: 'Feedback',
            action: (e: React.MouseEvent) => {
              e.preventDefault();
              window.open(import.meta.env.VITE_SENDFEEDBACK_URL, '_blank');
            }
          }
        ]
      }
    ],
    []
  );
  return (
    <DropdownMenu placement='right-start'>
      <DropdownMenu.Trigger
        asChild
        data-test-id='sidebar-support-dropdown-trigger'
      >
        <Sidebar.Item
          leadingIcon={<QuestionMarkCircleIcon />}
          classNames={{ leadingIcon: styles.infoicon }}
        >
          Information
        </Sidebar.Item>
      </DropdownMenu.Trigger>
      <CustomDropdownContent
        dropdownItems={dropdownItems}
        dataTestPrefix='sidebar-support-dropdown-'
      />
    </DropdownMenu>
  );
};

const IAMDialogContainer = () => {
  const dispatch = useDispatch();

  const { activeOrg } = useContext(IdentityContext);

  // @ts-expect-error redux state
  const IAMDialogState = useSelector(state => state.auth.IAMDialogState);

  const { getProjects } = useProjects({});

  async function closeIamDialog() {
    dispatch(setIAMDialogState({ isOpen: false, route: '' }));
    await getProjects(activeOrg?.id!);
  }

  return (
    <IAMDialog
      organizationId={activeOrg?.id!}
      onClose={closeIamDialog}
      dialogState={IAMDialogState}
    />
  );
};

const NotificationsContainer = forwardRef(
  function NotificationsContainer(_, ref) {
    // @ts-expect-error redux state
    const showNotifications = useSelector(state => state.app.showNotifications);

    return (
      <Show when={showNotifications}>
        <Notifications notificationIconRef={ref} />
      </Show>
    );
  }
);

const NewSidebar = () => {
  const { activeSubscription, isActiveSubscriptionLoading } = useFrontier();
  const { activeOrg, organizations, user } = useContext(IdentityContext);
  const [currentItem, setCurrentItem] = useState('explore');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const notificationIconRef = useRef(null);
  const orderDeskIconRef = useRef(null);

  // @ts-expect-error redux state
  const isSidebarOpen = useSelector(state => state.sourceActions.isSidebarOpen);

  const showUpgradeBtn = useMemo(() => {
    return (
      !isActiveSubscriptionLoading &&
      // activeSubscription will only be present when user is subscribed to paid plan
      activeSubscription?.state !== SubscriptionStateMap.ACTIVE
    );
  }, [activeSubscription?.state, isActiveSubscriptionLoading]);

  // preserving navigation bar state at all times
  useEffect(() => {
    let l = window.location.pathname;
    setCurrentItem(l);
  }, [navigate]);

  const navlinks = useMemo(
    () => [
      {
        key: 'explore',
        name: 'Explore',
        to: `/${activeOrg?.name}`,
        icon: <ExploreIcon />,
        routeMatch: '&'
      },

      {
        key: 'catalog',
        name: 'Catalog',
        to: `/${activeOrg?.name}/catalog`,
        icon: <CatalogIcon />,
        routeMatch: '/catalog'
      },
      {
        key: 'order-desk',
        name: 'Order Desk',
        to: `/${activeOrg?.name}/order-desk`,
        ref: orderDeskIconRef,
        icon: <OrderDeskIcon />,
        routeMatch: '/order-desk'
      },
      {
        key: 'cart',
        name: 'Cart',
        to: `/${activeOrg?.name}/cart`,
        icon: <CartSidebarIcon />,
        routeMatch: '/cart'
      },
      {
        key: 'aois',
        name: 'AOIs',
        to: `/${activeOrg?.name}/aois`,
        icon: <VisualizeIcon />,
        routeMatch: '/aois',
        onClick: () => dispatch(setActiveAoi({}))
      },
      {
        key: 'workflow',
        name: 'Workflow',
        to: `/${activeOrg?.name}/workflows`,
        icon: <WorkflowIcon />,
        routeMatch: '/workflows'
      },
      {
        key: 'marketplace',
        name: 'Marketplace',
        to: `/${activeOrg?.name}/marketplace`,
        icon: <MarketplaceIcon />,
        routeMatch: '/marketplace'
      }
      // {
      //   key: 'projects',
      //   name: 'Projects',
      //   href: '/',
      //   onClick: e => {
      //     e.preventDefault();
      //     dispatch(setIAMDialogState({ route: '/projects', isOpen: true }));
      //   },
      //   icon: ProjectsIcon
      // },
      // {
      //   key: 'team',
      //   name: 'Teams',
      //   href: '/',
      //   onClick: e => {
      //     e.preventDefault();
      //     dispatch(setIAMDialogState({ route: '/teams', isOpen: true }));
      //   },
      //   icon: TeamIcon
      // },
    ],
    [activeOrg?.name]
  );

  const bottomlinks = useMemo(
    () => [
      ...(showUpgradeBtn
        ? [
            {
              key: 'Upgrade',
              name: 'Upgrade',
              to: '/',
              onClick: (e: React.MouseEvent) => {
                e.preventDefault();
                dispatch(setIAMDialogState({ route: '/plans', isOpen: true }));
              },
              icon: <RocketIcon />
            }
          ]
        : []),
      {
        key: 'AuroraIntelligence',
        name: 'Aurora Intelligence',
        to: '/',
        onClick: (e: React.MouseEvent) => {
          e.preventDefault();
          dispatch(toggleCoPilot());
          dispatch(setCopilotChatId(null));
        },
        icon: <Avatar src={AURORA_INTELLIGENCE_THUMBNAIL_16} size={1} />
      },
      {
        key: 'Notifications',
        name: 'Notifications',
        to: '/',
        onClick: (e: React.MouseEvent) => {
          e.preventDefault();
          dispatch(toggleNotifications());
        },
        ref: notificationIconRef,
        icon: <NotificationIcon />
      }
    ],
    [showUpgradeBtn]
  );

  const handleSidebar = (val: boolean) => {
    dispatch(setIsSidebarOpen(val));
  };

  useKeyPressBase([219], () => handleSidebar(!isSidebarOpen));

  return (
    <Sidebar
      open={isSidebarOpen}
      onOpenChange={handleSidebar}
      collapsible={true}
      className={styles.sidebar}
      tooltipMessage={
        <>
          <Text weight='bold'>Click</Text> to{' '}
          {isSidebarOpen ? 'collapse' : 'expand'} ( [ )
        </>
      }
    >
      {/* need to ask cloud team to allow padding classname for header */}
      <Sidebar.Header style={{ padding: 'var(--rs-space-5) 0' }}>
        <OrgDropDown {...{ organizations, activeOrg, user }} />
      </Sidebar.Header>
      <Sidebar.Main>
        <For each={navlinks}>
          {nav => (
            <Sidebar.Item
              key={nav.name}
              ref={nav.ref}
              as={<NavLink to={nav.to} />}
              leadingIcon={nav.icon}
              classNames={{
                leadingIcon: styles.icon,
                root: styles.navItemContainer
              }}
              active={
                currentItem.includes(nav.routeMatch) || currentItem === nav.to
              }
              data-test-id={`sidebar-nav-link-${kebabCase(nav.key)}`}
            >
              {nav.name}
            </Sidebar.Item>
          )}
        </For>
      </Sidebar.Main>
      <Sidebar.Footer>
        <For each={bottomlinks}>
          {nav => (
            <Sidebar.Item
              key={nav.name}
              ref={nav.ref}
              href={nav.to}
              leadingIcon={nav.icon}
              classNames={{ leadingIcon: styles.icon }}
              onClick={nav.onClick}
              data-test-id={`sidebar-footer-${kebabCase(nav.key)}`}
            >
              {nav.name}
            </Sidebar.Item>
          )}
        </For>
        <SupportDropdown />
        <ProfileDropDown user={user} />
      </Sidebar.Footer>
      <IAMDialogContainer />
      <NotificationsContainer ref={notificationIconRef} />
      <OrderDeskPromotionPopup
        getTriggerElement={() => orderDeskIconRef.current}
        onOpenChange={open => {
          if (orderDeskIconRef.current) {
            (orderDeskIconRef.current as HTMLElement).setAttribute(
              'data-elevate',
              `${open}`
            );
          }
        }}
      />
    </Sidebar>
  );
};

export default NewSidebar;
